import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { CloudDownloadOutlined, SaveOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import {
  useRssConfig,
  useRunRssImport,
  useUpdateRssConfig,
  useVerifyRssImages,
} from '@shared/api/hooks/useRss';

interface FormValues {
  feedUrl: string;
  enabled: boolean;
  autoPublish: boolean;
}

export function RssImportPage(): React.ReactElement {
  const { data: config, isLoading } = useRssConfig();
  const updateMutation = useUpdateRssConfig();
  const runMutation = useRunRssImport();
  const verifyMutation = useVerifyRssImages();
  const [form] = Form.useForm<FormValues>();
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (config) {
      form.setFieldsValue({
        feedUrl: config.feedUrl,
        enabled: config.enabled,
        autoPublish: config.autoPublish,
      });
      setDirty(false);
    }
  }, [config, form]);

  const onSave = async (): Promise<void> => {
    const v = await form.validateFields();
    try {
      await updateMutation.mutateAsync({
        feedUrl: v.feedUrl,
        enabled: v.enabled,
        autoPublish: v.autoPublish,
      });
      message.success('Configurazione salvata');
      setDirty(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore di salvataggio'));
    }
  };

  const onRun = async (): Promise<void> => {
    try {
      const r = await runMutation.mutateAsync();
      message.success(
        `Import completato: ${r.imported} importati, ${r.skipped} già presenti` +
          (r.failed ? `, ${r.failed} falliti` : ''),
      );
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Import fallito');
    }
  };

  const onVerifyImages = async (): Promise<void> => {
    try {
      const r = await verifyMutation.mutateAsync();
      const parts = [`${r.checked} controllate`, `${r.repaired} ripristinate`];
      if (r.missingOnSource) parts.push(`${r.missingOnSource} assenti sulla sorgente`);
      if (r.unresolved) parts.push(`${r.unresolved} non risolte`);
      message.success(`Verifica completata: ${parts.join(', ')}`);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Verifica immagini fallita');
    }
  };

  const statusTag = (s: string | null): React.ReactNode => {
    if (!s) return <Typography.Text type="secondary">—</Typography.Text>;
    return <Tag color={s === 'OK' ? 'green' : 'red'}>{s}</Tag>;
  };

  return (
    <Card
      loading={isLoading}
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Import articoli da RSS
        </Typography.Title>
      }
      extra={
        <Space>
          <Button
            icon={<SafetyCertificateOutlined />}
            loading={verifyMutation.isPending}
            onClick={onVerifyImages}
          >
            Verifica immagini
          </Button>
          <Button
            type="primary"
            icon={<CloudDownloadOutlined />}
            loading={runMutation.isPending}
            onClick={onRun}
          >
            Importa ora
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        message="Gli articoli importati finiscono nella sezione News & Post. Con «Pubblica automaticamente» disattivo restano bozze da rivedere; se attivo vengono pubblicati subito e inviano la push NEW_POST ai tifosi. «Verifica immagini» ricontrolla tutte le copertine e ripristina dalla sorgente quelle mancanti o corrotte (gira anche da solo ogni ora)."
        style={{ marginBottom: 16 }}
      />

      <Form<FormValues>
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onValuesChange={() => setDirty(true)}
      >
        <Form.Item
          name="feedUrl"
          label="URL del feed RSS"
          rules={[
            { required: true, message: 'Inserisci l’URL del feed' },
            { type: 'url', message: 'URL non valido' },
            { max: 500 },
          ]}
        >
          <Input placeholder="https://...?format=feed&type=rss" />
        </Form.Item>

        <Form.Item
          name="enabled"
          label="Import automatico (ogni ora)"
          valuePropName="checked"
          tooltip="Se attivo, un job orario importa automaticamente i nuovi articoli"
        >
          <Switch checkedChildren="Attivo" unCheckedChildren="Disattivo" />
        </Form.Item>

        <Form.Item
          name="autoPublish"
          label="Pubblica automaticamente"
          valuePropName="checked"
          tooltip="Se attivo gli articoli importati vengono pubblicati subito e inviano la push NEW_POST ai tifosi. Se disattivo restano bozze da rivedere."
        >
          <Switch checkedChildren="Pubblica" unCheckedChildren="Bozza" />
        </Form.Item>

        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={updateMutation.isPending}
            disabled={!dirty}
            onClick={onSave}
          >
            Salva configurazione
          </Button>
        </Space>
      </Form>

      <Descriptions title="Ultimo import" column={1} style={{ marginTop: 24, maxWidth: 640 }} bordered size="small">
        <Descriptions.Item label="Data">
          {config?.lastImportedAt
            ? new Date(config.lastImportedAt).toLocaleString('it-IT')
            : <Typography.Text type="secondary">mai eseguito</Typography.Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Esito">{statusTag(config?.lastStatus ?? null)}</Descriptions.Item>
        {config?.lastError ? (
          <Descriptions.Item label="Errore">
            <Typography.Text type="danger">{config.lastError}</Typography.Text>
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    </Card>
  );
}
